import { useCallback } from 'react';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { speechToText, ttsQueue } from '@/services/elevenlabs';
import { STTResult } from '@/types';
import { useToast } from './use-toast';

export function useElevenLabs() {
  const { config } = useAppConfig();
  const { toast } = useToast();

  /**
   * Play text as speech (add to queue)
   */
  const playTTS = useCallback(
    async (text: string) => {
      // Check if voice mode is enabled
      if (!config.voiceModeEnabled) {
        console.log('TTS: Voice mode disabled, skipping');
        return;
      }

      if (!config.voiceAPIKey) {
        console.error('Voice API key not configured');
        toast({
          title: 'Error',
          description: 'Voice API key not configured',
          variant: 'destructive',
        });
        return;
      }

      if (!text || text.trim().length === 0) {
        console.warn('Empty text, skipping TTS');
        return;
      }

      try {
        await ttsQueue.add(text, config.voiceId, config.voiceAPIKey, config.ttsModel);
      } catch (error) {
        console.error('TTS error:', error);
        toast({
          title: 'TTS Error',
          description: error instanceof Error ? error.message : 'Failed to play audio',
          variant: 'destructive',
        });
      }
    },
    [config.voiceModeEnabled, config.voiceAPIKey, config.voiceId, config.ttsModel, toast]
  );

  /**
   * Convert audio to text
   */
  const transcribeAudio = useCallback(
    async (audioBlob: Blob): Promise<STTResult | null> => {
      // Check if voice mode is enabled
      if (!config.voiceModeEnabled) {
        console.log('STT: Voice mode disabled, skipping');
        return null;
      }

      if (!config.voiceAPIKey) {
        console.error('Voice API key not configured');
        toast({
          title: 'Error',
          description: 'Voice API key not configured',
          variant: 'destructive',
        });
        return null;
      }

      try {
        const result = await speechToText(audioBlob, config.voiceAPIKey, config.sttModel);
        return result;
      } catch (error) {
        console.error('STT error:', error);
        toast({
          title: 'STT Error',
          description: error instanceof Error ? error.message : 'Failed to transcribe audio',
          variant: 'destructive',
        });
        return null;
      }
    },
    [config.voiceModeEnabled, config.voiceAPIKey, config.sttModel, toast]
  );

  /**
   * Stop current TTS playback
   */
  const stopTTS = useCallback(() => {
    ttsQueue.stop();
  }, []);

  /**
   * Clear TTS queue
   */
  const clearTTSQueue = useCallback(() => {
    ttsQueue.clear();
  }, []);

  return {
    playTTS,
    transcribeAudio,
    stopTTS,
    clearTTSQueue,
  };
}
